import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { Bot, User, Briefcase, Sparkles, ArrowRight, CheckCircle, Loader2 } from "lucide-react";
import Layout from "../components/layout/Layout";
import { useAuth } from "../context/AuthContext";

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, profile, updateUser } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Step 1: Profiling
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [position, setPosition] = useState("");

  // Step 2: Workspace
  const [company, setCompany] = useState("");

  // Step 3: First Task
  const [taskText, setTaskText] = useState("");
  const [taskDraft, setTaskDraft] = useState(null);

  useEffect(() => {
    if (profile) {
      if (profile.name) {
        const parts = profile.name.split(" ");
        setFirstName(parts[0] || "");
        setLastName(parts.slice(1).join(" ") || "");
      }
      if (profile.title || profile.job_title) {
        setPosition(profile.title || profile.job_title);
      }
      if (profile.company_name) {
        setCompany(profile.company_name);
      }
    }
  }, [profile]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!firstName) return;
    
    setLoading(true);
    setError("");
    try {
      const fullName = `${firstName} ${lastName}`.trim();
      await updateUser({ name: fullName, position });
      setStep(2);
    } catch (err) {
      setError(err.message || "Ошибка при сохранении профиля");
    } finally {
      setLoading(false);
    }
  };

  const handleWorkspaceSubmit = async (e) => {
    e.preventDefault();
    if (!company) return;
    
    setLoading(true);
    setError("");
    try {
      // This will also trigger workspace creation on the backend
      await updateUser({ company });
      setStep(3);
    } catch (err) {
      setError(err.message || "Ошибка при создании проекта");
    } finally {
      setLoading(false);
    }
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    if (!taskText) return;
    
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("obstuchim_token");
      const res = await fetch("/api/ai/copilot/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ message: taskText })
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Ошибка генерации");
      
      if (data.draftResult) {
        setTaskDraft(data.draftResult.draft);
      } else {
        // Fallback if AI didn't explicitly call create_task_draft
        setTaskDraft({
          title: "Разобрать новую инициативу",
          description: taskText,
        });
      }
      setStep(4);
    } catch (err) {
      setError(err.message || "Ошибка при анализе задачи");
    } finally {
      setLoading(false);
    }
  };

  const slideVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: "easeOut" } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.3 } }
  };

  return (
    <Layout>
      <div className="min-h-[85vh] flex flex-col pt-12 lg:pt-20 pb-12">
        <div className="max-w-2xl mx-auto w-full px-4 sm:px-6">
          
          {/* AI Assistant Avatar Header */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 mb-10"
          >
            <div className="w-14 h-14 bg-primary-900/30 border border-primary-500/30 rounded-full flex items-center justify-center shrink-0">
              <Bot className="w-7 h-7 text-primary-400" />
            </div>
            <div>
              <h2 className="text-xl font-light text-neutral-200">ИИ-Помощник</h2>
              <p className="text-neutral-500 font-extralight text-sm">Настройка вашего профиля</p>
            </div>
          </motion.div>

          <div className="bg-neutral-900/40 border border-neutral-800/50 rounded-2xl p-6 sm:p-10 backdrop-blur-sm relative overflow-hidden">
            
            {/* Progress Bar */}
            <div className="absolute top-0 left-0 w-full h-1 bg-neutral-800">
              <div 
                className="h-full bg-primary-600 transition-all duration-500 ease-out"
                style={{ width: `${((step - 1) / 3) * 100}%` }}
              />
            </div>

            <AnimatePresence mode="wait">
              
              {/* STEP 1: PROFILING */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  variants={slideVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <h3 className="text-2xl font-light text-neutral-100 mb-2">
                    Добро пожаловать в «Обстучим»
                  </h3>
                  <p className="text-neutral-400 font-extralight mb-8">
                    Давайте настроим ваше рабочее пространство, чтобы я мог помогать вам эффективнее.
                  </p>

                  <form onSubmit={handleProfileSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-extralight text-neutral-400 mb-2">Имя</label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600" />
                          <input
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            required
                            placeholder="Александр"
                            className="w-full pl-12 pr-4 py-3 rounded-md bg-neutral-950 border border-neutral-800 text-neutral-100 font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-700 transition-colors"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-extralight text-neutral-400 mb-2">Фамилия</label>
                        <input
                          type="text"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          placeholder="Иванов"
                          className="w-full px-4 py-3 rounded-md bg-neutral-950 border border-neutral-800 text-neutral-100 font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-700 transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-extralight text-neutral-400 mb-2">Ваша роль / Должность</label>
                      <input
                        type="text"
                        value={position}
                        onChange={(e) => setPosition(e.target.value)}
                        placeholder="Основатель, CEO, Директор по маркетингу..."
                        className="w-full px-4 py-3 rounded-md bg-neutral-950 border border-neutral-800 text-neutral-100 font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-700 transition-colors"
                      />
                    </div>

                    {error && <p className="text-red-400 text-sm">{error}</p>}

                    <div className="pt-4 flex justify-end">
                      <button
                        type="submit"
                        disabled={loading || !firstName}
                        className="px-8 py-3 rounded-md bg-primary-700 hover:bg-primary-600 disabled:opacity-50 text-white font-light transition-colors flex items-center gap-2"
                      >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Продолжить"}
                        {!loading && <ArrowRight className="w-5 h-5" />}
                      </button>
                    </div>
                  </form>
                </motion.div>
              )}

              {/* STEP 2: WORKSPACE */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  variants={slideVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <h3 className="text-2xl font-light text-neutral-100 mb-2">
                    Создание проекта
                  </h3>
                  <p className="text-neutral-400 font-extralight mb-8">
                    Укажите название вашей компании или текущего проекта. Мы создадим для него изолированное рабочее пространство.
                  </p>

                  <form onSubmit={handleWorkspaceSubmit} className="space-y-5">
                    <div>
                      <label className="block text-sm font-extralight text-neutral-400 mb-2">Название компании / проекта</label>
                      <div className="relative">
                        <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600" />
                        <input
                          type="text"
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                          required
                          placeholder="Например, Обстучим"
                          className="w-full pl-12 pr-4 py-3 rounded-md bg-neutral-950 border border-neutral-800 text-neutral-100 font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-700 transition-colors"
                        />
                      </div>
                    </div>

                    {error && <p className="text-red-400 text-sm">{error}</p>}

                    <div className="pt-4 flex justify-between items-center">
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="text-neutral-500 hover:text-neutral-300 font-extralight transition-colors"
                      >
                        Назад
                      </button>
                      <button
                        type="submit"
                        disabled={loading || !company}
                        className="px-8 py-3 rounded-md bg-primary-700 hover:bg-primary-600 disabled:opacity-50 text-white font-light transition-colors flex items-center gap-2"
                      >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Создать проект"}
                        {!loading && <ArrowRight className="w-5 h-5" />}
                      </button>
                    </div>
                  </form>
                </motion.div>
              )}

              {/* STEP 3: AHA MOMENT - FIRST TASK */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  variants={slideVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <h3 className="text-2xl font-light text-neutral-100 mb-2">
                    Отлично, {firstName}!
                  </h3>
                  <p className="text-neutral-400 font-extralight mb-8 leading-relaxed">
                    Рабочее пространство создано. Наверняка у вас есть задача, которую вы откладываете или которую нужно кому-то поручить. 
                    <span className="text-neutral-200"> Напишите её своими словами, как есть</span>, а я помогу её упаковать.
                  </p>

                  <form onSubmit={handleTaskSubmit} className="space-y-5">
                    <div>
                      <textarea
                        value={taskText}
                        onChange={(e) => setTaskText(e.target.value)}
                        required
                        rows={4}
                        placeholder="Например: Надо срочно переделать презентацию для инвесторов, там цифры старые. Не знаю, кому отдать, может нашему аналитику."
                        className="w-full px-4 py-4 rounded-md bg-neutral-950 border border-neutral-800 text-neutral-100 font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-700 transition-colors resize-none"
                      />
                    </div>

                    {error && <p className="text-red-400 text-sm">{error}</p>}

                    <div className="pt-2 flex justify-between items-center">
                      <button
                        type="button"
                        onClick={() => navigate("/workspace")}
                        className="text-neutral-500 hover:text-neutral-300 font-extralight transition-colors"
                      >
                        Пропустить
                      </button>
                      <button
                        type="submit"
                        disabled={loading || !taskText}
                        className="px-8 py-3 rounded-md bg-primary-700 hover:bg-primary-600 disabled:opacity-50 text-white font-light transition-colors flex items-center gap-2"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>ИИ-Помощник анализирует...</span>
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5" />
                            <span>Упаковать задачу</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </motion.div>
              )}

              {/* STEP 4: RESULT */}
              {step === 4 && (
                <motion.div
                  key="step4"
                  variants={slideVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-primary-900/30 border border-primary-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-primary-400" />
                  </div>
                  
                  <h3 className="text-2xl font-light text-neutral-100 mb-2">
                    Задача успешно упакована
                  </h3>
                  <p className="text-neutral-400 font-extralight mb-8">
                    Мы создали черновик. Вы сможете отредактировать его и назначить исполнителя в панели управления.
                  </p>

                  {taskDraft && (
                    <div className="bg-neutral-950 border border-neutral-800 rounded-lg p-5 text-left mb-8 max-w-lg mx-auto">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-4 h-4 text-primary-500" />
                        <span className="text-sm text-primary-400 font-medium">Черновик от ИИ</span>
                      </div>
                      <h4 className="text-lg text-neutral-200 font-light mb-2">{taskDraft.title}</h4>
                      <p className="text-neutral-500 font-extralight text-sm line-clamp-3">
                        {taskDraft.description}
                      </p>
                    </div>
                  )}

                  <button
                    onClick={() => navigate("/workspace")}
                    className="w-full sm:w-auto px-8 py-4 rounded-md bg-primary-700 hover:bg-primary-600 text-white font-light transition-colors flex items-center justify-center gap-2 mx-auto"
                  >
                    Сохранить и перейти в проект
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </motion.div>
              )}

            </AnimatePresence>
          </div>

        </div>
      </div>
    </Layout>
  );
}
